import { CancelOutlined, CheckCircleOutline } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  TableContainer,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import SimpleDialog from '../../../../common-components/dialog/SimpleDialog';
import {
  AddMyFleetVesselsResponse,
  MyFleetDialogStatus,
  MyFleetVessels,
} from '../../../../models/my-fleet.model';
import { ProAlertCreateResponse } from '../../../../models/pro.model';
import MyFleetVesselErrorTable from '../../my-fleet-vessel-error-table/my-fleet-vessel-error-table';

interface CreateMyFleetVesselsProps {
  open: boolean;
  onClose: () => void;
  status: MyFleetDialogStatus;
  response: AddMyFleetVesselsResponse | null;
  alertsResponse: ProAlertCreateResponse | null;
  vessels: MyFleetVessels | null;
  batchProgress?: {
    current: number;
    total: number;
    completed: number;
    failed: number;
  };
  batchErrors?: any[];
}

function MyFleetVesselsCreateDialog({
  open,
  onClose,
  status,
  response,
  alertsResponse,
  vessels,
  batchProgress,
  batchErrors,
}: CreateMyFleetVesselsProps) {
  const CONTENT_MIN_HEIGHT = 80;
  const CONTENT_MAX_HEIGHT = 200;
  const CONTENT_MAX_WIDTH = 500;
  const TABLE_MIN_HEIGHT = 40;
  const TABLE_MAX_HEIGHT = 120;
  const isErrorResponseArray =
    response && Array.isArray(response.errors) && response.errors.length > 0;

  const renderTitle = () => {
    switch (status) {
      case MyFleetDialogStatus.VesselError:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelOutlined sx={{ color: 'error.main' }} />
            <Typography data-testid="add-vessels-dialog-error-content">
              {response?.message}
            </Typography>
          </Box>
        );
      case MyFleetDialogStatus.AlertError:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelOutlined sx={{ color: 'error.main' }} />
            <Typography data-testid="add-vessels-dialog-error-content">
              {alertsResponse?.message}
            </Typography>
          </Box>
        );
      case MyFleetDialogStatus.Success:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutline sx={{ color: 'secondary.main' }} />
            <Typography data-testid="add-vessels-dialog-content">
              {response?.message}
            </Typography>
          </Box>
        );
      case MyFleetDialogStatus.CreatingVessels:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} sx={{ color: 'secondary.main' }} />
            <Typography data-testid="add-vessels-dialog-loading">
              {batchProgress && batchProgress.total > 0
                ? `Creating vessels: ${batchProgress.completed} of ${batchProgress.total}`
                : 'Creating vessels:'}
            </Typography>
          </Box>
        );
      case MyFleetDialogStatus.CreatingAlerts:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} sx={{ color: 'secondary.main' }} />
            <Typography data-testid="add-vessels-dialog-loading">
              {batchProgress && batchProgress.total > 0
                ? `Creating alerts: ${batchProgress.completed} of ${batchProgress.total}`
                : 'Creating alerts:'}
            </Typography>
          </Box>
        );
      default:
        return <Typography>Create my fleet vessels</Typography>;
    }
  };

  const getNumProAlerts = () =>
    alertsResponse && alertsResponse?.alerts?.length
      ? alertsResponse.alerts.length
      : 0;

  const renderActions = () => (
    <Stack
      direction="column"
      spacing={1}
      justifyContent={
        status === MyFleetDialogStatus.Loading ? 'flex-end' : 'space-between'
      }
      sx={{ width: '100%' }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
        data-testid="add-vessels-dialog-actions"
      >
        {status === MyFleetDialogStatus.Success && (
          <Typography sx={{ alignItems: 'flex-start', width: '100%' }}>
            {getNumProAlerts()
              ? `${getNumProAlerts()} PRO alerts created.`
              : 'Remember to set up alerts for these vessels.'}
          </Typography>
        )}
        {status === MyFleetDialogStatus.VesselError && (
          <Typography sx={{ alignItems: 'flex-start', width: '100%' }}>
            No vessels have been created.
          </Typography>
        )}
        {status === MyFleetDialogStatus.AlertError && (
          <Typography sx={{ alignItems: 'flex-start', width: '100%' }}>
            Vessels created, please check alerts.
          </Typography>
        )}
        <Button
          onClick={onClose}
          data-testid="add-vessels-dialog-close-button"
          sx={{ mb: 0 }}
          disabled={
            status === MyFleetDialogStatus.Loading ||
            status === MyFleetDialogStatus.CreatingVessels ||
            status === MyFleetDialogStatus.CreatingAlerts
          }
        >
          Close
        </Button>
      </Box>
    </Stack>
  );

  const renderContent = () => (
    <Box
      sx={{
        minHeight: CONTENT_MIN_HEIGHT,
        maxHeight: CONTENT_MAX_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mt: 1,
          mb: 1,
          flexGrow: 1,
        }}
      >
        {renderTitle()}
      </Box>
      {status !== MyFleetDialogStatus.Loading &&
        status === MyFleetDialogStatus.VesselError &&
        isErrorResponseArray && (
          <TableContainer
            sx={{
              minHeight: `${TABLE_MIN_HEIGHT}px`,
              maxHeight: `${TABLE_MAX_HEIGHT}px`,
              mt: 1,
              mb: 2,
              scrollbarWidth: 'thin',
            }}
          >
            <MyFleetVesselErrorTable
              errors={response?.errors || batchErrors || []}
              vessels={vessels}
            />
          </TableContainer>
        )}
    </Box>
  );

  return (
    <SimpleDialog
      onClose={onClose}
      open={open}
      title="Create Vessels"
      width={CONTENT_MAX_WIDTH}
      actions={renderActions()}
      data-testid="add-vessels-dialog"
      disableBackdropClick
      disableEscapeKeyDown
    >
      {renderContent()}
    </SimpleDialog>
  );
}

MyFleetVesselsCreateDialog.defaultProps = {
  batchProgress: undefined,
  batchErrors: [],
};
export default MyFleetVesselsCreateDialog;
